import { DTO } from '../../utilities/Dto';

export interface User {
  recnum: number;
  name: string;
  internal: boolean;
  active: boolean;
  loginname: string;
}

/**
 * Departments a user can belong to. Mirrors the legacy frontend's
 * `Department` enum; stored as free text in `cloud_usr.department`.
 */
export const DEPARTMENTS = [
  'climate',
  'electrical',
  'greenhouse',
  'irrigation',
  'service',
  'climate-cells',
] as const;
export type Department = (typeof DEPARTMENTS)[number];

/** Languages the application and its mails exist in (`cloud_usr.language`). */
export const LANGUAGES = ['en', 'nl'] as const;
export type Language = (typeof LANGUAGES)[number];

export interface SessionUser extends User {
  email: string;
  /** Address awaiting confirmation through the verification mail, if any */
  email_new: string | null;
  language: string;
  permissions: number;
  department: Department | null;
  internal: boolean;

  settings: any /* FIXME: use usersettings type */;
  tag_id: null | string;
  requested_tag_id: null | string;
  requested_tag_on: null | Date;
  expires_on: string | null;
}

export interface ClientInfo {
  // Client device info
  client_id: string;
  useragent: string;
  version: string | null;
  created: string;
  last_contact: string;

  // Session info (from qa.sessions - web-next clients)
  refresh_token_expires: string | null;
  access_token_expires: string | null;
  auth_method: 'password' | 'tag' | 'token' | null;
  session_created: string | null;

  // Sync info (from qa.syncresults - clients that have synced)
  sync_success: boolean | null;
  sync_date: string | null;
  sync_meta: any | null;
}

export interface AdminAccount extends SessionUser {
  // Identity (additional to SessionUser)
  contact: string;
  relat: string;
  note: string;
  activated: boolean;

  // Security metrics
  password_strength: number | null;
  password_date: string | null;
  last_successful_login: string | null;
  auth_attempts: number;

  // Tag authentication (additional to SessionUser)
  tag_authentication_preapproved: boolean;
  tag_authentication_preapproved_expires_on: string | null;

  // Related data (detail view only)
  clientInfo?: ClientInfo[];
}

export interface AdminAccountPatch {
  recnum: number;
  name?: string;
  activated?: boolean;
  note?: string;
  permissions?: number;
  auth_attempts?: number;
  expires_on?: string | 'extend';
  tag_id?: string | null;
  tag_authentication_preapproved?: boolean;
  enable_requested_tag_id?: boolean;
  // Action fields (mutually exclusive)
  send_invitation?: boolean;
  force_reactivation?: boolean;
  force_password_reset?: boolean;
  reactivation_reason?: string;
  bcc?: string;
}

export function parseAdminAccountDto(account: DTO<AdminAccount>): AdminAccount {
  return {
    ...account,
    requested_tag_on: account.requested_tag_on
      ? new Date(account.requested_tag_on)
      : null,
  };
}
